// frontend/src/StoreLocator/Locations.jsx — the list tab.
import React, { useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { InlineStack, Select, Tag } from '@shopify/polaris';
import { StatusPill } from '../ui';
import { Alert, useList } from '../GiftCards/common';
import { slApi, openBlob } from './apiClient';
import { hoursText, openText } from './common';

export default function Locations({ ctx }) {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { data, error, reload } = useList(() => slApi.locations(), []);
  const [busy, setBusy] = useState('');
  const [importResult, setImportResult] = useState(null);
  const fileInput = useRef(null);
  const q = params.get('q') || ''; const state = params.get('state') || ''; const status = params.get('status') || 'all'; const tag = params.get('tag') || '';
  const set = (k, v) => { const p = new URLSearchParams(params); if (v) p.set(k, v); else p.delete(k); setParams(p, { replace: true }); };
  const rows = useMemo(() => {
    const all = (data && data.locations) || [];
    const needle = q.trim().toLowerCase();
    return all.filter((l) => (!needle || [l.name, l.city, l.region, l.address_line_1, l.postal_code, l.handle].some((v) => (v || '').toLowerCase().includes(needle)))
      && (!state || l.region === state) && (status === 'all' || l.status === status) && (!tag || (l.tags || []).includes(tag)));
  }, [data, q, state, status, tag]);
  const stores = ((ctx.meta && ctx.meta.stores) || []).filter((s) => s.enabled);
  const onStores = (l) => {
    if (l.status !== 'published') return '—';
    const n = stores.filter((s) => s.last_published_at && s.last_result && s.last_result.ok && new Date(s.last_published_at) >= new Date(l.updated_at)).length;
    return `${n} of ${stores.length}`;
  };
  const importCsv = async (file) => {
    if (!file) return;
    setBusy('import'); setImportResult(null);
    try { const r = await slApi.importCsv(file); setImportResult(r); ctx.notify(`${r.added} added, ${r.updated} updated`); reload(); ctx.reloadMeta(); } catch (e) { setImportResult({ error: e.message }); } finally { setBusy(''); }
  };
  const exportCsv = async () => { setBusy('export'); try { openBlob(await slApi.exportCsv(), 'store-locations.csv'); } catch (e) { ctx.notify(e.message); } finally { setBusy(''); } };
  const counts = (ctx.meta && ctx.meta.counts) || {};
  const lastPublish = stores.map((s) => s.last_published_at).filter(Boolean).sort().pop();
  return (
    <div className="uh-card">
      <div className="uh-ia-tbar">
        <input className="uh-ia-inp" style={{ maxWidth: 280 }} placeholder="Search name, city, address…" value={q} onChange={(e) => set('q', e.target.value)} aria-label="Search locations" />
        <Select label="State" labelHidden value={state} onChange={(v) => set('state', v)} options={[{ value: '', label: 'All states' }, ...((ctx.meta && ctx.meta.states) || []).map((s) => ({ value: s, label: s }))]} />
        <Select label="Status" labelHidden value={status} onChange={(v) => set('status', v === 'all' ? '' : v)} options={[{ value: 'all', label: 'Published and draft' }, { value: 'published', label: 'Published' }, { value: 'draft', label: 'Draft' }]} />
        <Select label="Tag" labelHidden value={tag} onChange={(v) => set('tag', v)} options={[{ value: '', label: 'Any tag' }, ...((ctx.meta && ctx.meta.tags) || []).map((t) => ({ value: t.title, label: t.title }))]} />
        <span className="uh-sp" />
        <button type="button" className="uh-btn" disabled={!!busy} onClick={() => fileInput.current && fileInput.current.click()}>{busy === 'import' ? 'Importing…' : 'Import CSV'}</button>
        <input ref={fileInput} type="file" accept=".csv,text/csv" hidden onChange={(e) => { importCsv(e.target.files[0]); e.target.value = ''; }} />
        <button type="button" className="uh-btn" disabled={!!busy} onClick={exportCsv}>Export CSV</button>
        <button type="button" className="uh-btn primary" onClick={() => navigate('/store-locator/new')}>Add location</button>
      </div>
      <div className="uh-card-b">
        <Alert>{error}</Alert>
        {importResult && (importResult.error ? <Alert>{importResult.error}</Alert> : (importResult.problems || []).length > 0 && <div className="uh-ia-warn">{importResult.problems.length} rows skipped: {importResult.problems.join('; ')}</div>)}
        <div className="uh-ia-stats" style={{ marginBottom: 12 }}>
          <div className="stat"><span className="v">{counts.locations ?? '—'}</span><span className="k">Locations</span></div>
          <div className="stat"><span className="v">{counts.published ?? '—'}</span><span className="k">Published</span></div>
          <div className="stat"><span className="v">{counts.states ?? '—'}</span><span className="k">States</span></div>
          <div className="stat"><span className="v">{lastPublish ? new Date(lastPublish).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : 'Never'}</span><span className="k">Last publish</span></div>
        </div>
        <div className="uh-tscroll">
          <table className="uh-tbl uh-sl-tbl">
            <thead><tr><th>Name</th><th>City</th><th>State</th><th>Today</th><th>Tags</th><th>Priority</th><th>Status</th><th>On stores</th></tr></thead>
            <tbody>
              {rows.map((l) => (
                <tr key={l.id} className="uh-row-link" onClick={() => navigate(`/store-locator/locations/${l.id}`)}>
                  <td><b>{l.name}</b><div className="uh-faint uh-ia-small">{l.address_line_1}</div></td>
                  <td>{l.city}</td><td>{l.region}</td>
                  <td>{hoursText(l.today)}<div className="uh-faint uh-ia-small">{openText(l.open_state)}{l.upcoming_special && l.upcoming_special.length > 0 ? ` · special hours ${l.upcoming_special[0].date.slice(5).replace('-', '/')}` : ''}</div></td>
                  <td><InlineStack gap="100" wrap>{(l.tags || []).map((t) => <Tag key={t}>{t}</Tag>)}</InlineStack></td>
                  <td className="uh-num">{l.priority}</td>
                  <td><StatusPill tone={l.status === 'published' ? 'ful' : 'hold'}>{l.status === 'published' ? 'Published' : 'Draft'}</StatusPill></td>
                  <td>{onStores(l)}</td>
                </tr>
              ))}
              {data && rows.length === 0 && <tr><td colSpan={8} className="uh-empty">{(data.locations || []).length ? 'No location matches those filters.' : 'No locations yet. Import the CSV or add one.'}</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
